import { useMemo } from "react";
import { useLocalizedFields, useLocalizedList } from "./useLocalizedContent";
import type { GUIDES } from "@/lib/seoClusters";

type Guide = (typeof GUIDES)[number];

/**
 * Localizes a `/guides/:slug` page's prose (title, intro, body headings +
 * paragraphs, and FAQ Q/A) via the existing `translate-content` edge function
 * pipeline used by other localized content. English is a no-op; other locales
 * fetch translations and cache them in `content_translations` server-side.
 *
 * Returns a `Guide`-shaped object with translated text for the current locale
 * plus a boolean that indicates whether translations are still resolving so
 * callers can decide to render the English fallback in the meantime.
 */
export function useLocalizedGuide(guide: Guide): {
  guide: Guide;
  isLoading: boolean;
} {
  // 1) Top-level fields — title, intro, and (for meta parity) description.
  const topLevel = useLocalizedFields("guide", guide.slug, {
    title: guide.title,
    label: guide.label,
    description: guide.description,
    intro: guide.intro,
  });

  // 2) Body sections — flatten into a translation list keyed by section index.
  const bodyItems = useMemo(() => {
    if (!guide.body?.length) return [];
    return guide.body.flatMap((section, sIdx) => [
      {
        entityType: "guide-body",
        entityId: `${guide.slug}:${sIdx}`,
        field: "heading",
        sourceText: section.heading,
      },
      ...section.paragraphs.map((p, pIdx) => ({
        entityType: "guide-body",
        entityId: `${guide.slug}:${sIdx}`,
        field: `p${pIdx}`,
        sourceText: p,
      })),
    ]);
  }, [guide]);
  const bodyTr = useLocalizedList(bodyItems);

  // 3) FAQs — question + answer per item.
  const faqItems = useMemo(() => {
    if (!guide.faqs?.length) return [];
    return guide.faqs.flatMap((f, i) => [
      {
        entityType: "guide-faq",
        entityId: `${guide.slug}:${i}`,
        field: "q",
        sourceText: f.question,
      },
      {
        entityType: "guide-faq",
        entityId: `${guide.slug}:${i}`,
        field: "a",
        sourceText: f.answer,
      },
    ]);
  }, [guide]);
  const faqTr = useLocalizedList(faqItems);

  const localized: Guide = useMemo(() => {
    const out: Guide = {
      ...guide,
      title: topLevel.values["title"] || guide.title,
      label: topLevel.values["label"] || guide.label,
      description: topLevel.values["description"] || guide.description,
      intro: topLevel.values["intro"] || guide.intro,
      body: guide.body?.map((section, sIdx) => ({
        ...section,
        heading:
          bodyTr.map.get(`${guide.slug}:${sIdx}::heading`) ?? section.heading,
        paragraphs: section.paragraphs.map(
          (p, pIdx) =>
            bodyTr.map.get(`${guide.slug}:${sIdx}::p${pIdx}`) ?? p,
        ),
      })),
      faqs: guide.faqs?.map((f, i) => ({
        question: faqTr.map.get(`${guide.slug}:${i}::q`) ?? f.question,
        answer: faqTr.map.get(`${guide.slug}:${i}::a`) ?? f.answer,
      })),
    };
    return out;
  }, [guide, topLevel.values, bodyTr.map, faqTr.map]);

  return {
    guide: localized,
    isLoading: topLevel.isLoading || bodyTr.isLoading || faqTr.isLoading,
  };
}
